import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle2, LogIn, User, Lock, Loader2, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ConclaveRegistration = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [professional, setProfessional] = useState(null);
  
  const [formData, setFormData] = useState({
    professionalId: '',
    fullName: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    country: '',
    occupation: '',
    organization: '',
    designation: '',
    areasOfInterest: [],
    interestedInEvent: true,
    attendancePreference: [],
    specialRequirements: '',
    accompanyingPersons: '',
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const interestAreas = [
    'Networking & Business Connections',
    'Community Development',
    'Youth Empowerment',
    'Career Guidance',
    'Entrepreneurship',
    'Social Welfare',
    'Political Participation',
    'Educational Advancement',
    'Matrimonial Connections',
  ];

  const sessionPreferences = [
    'Panel Discussions',
    'Networking Sessions',
    'Career Guidance Workshops',
    'Entrepreneurship Talks',
    'Matrimonial Interaction Segment',
    'Cultural Programs',
    'Community Dialogues',
  ];

  // Check if professional is logged in
  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    const token = localStorage.getItem('professionalToken');
    
    if (!token) {
      setIsAuthenticated(false);
      setCheckingAuth(false);
      return;
    }

    try {
      const response = await fetch(`${API}/professionals/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProfessional(data);
        setIsAuthenticated(true);
        
        // Pre-fill form with professional's data
        setFormData(prev => ({
          ...prev,
          professionalId: data.professionalId || data.id,
          fullName: data.fullName || '',
          email: data.email || '',
          phone: data.phone || '',
          city: data.city || '',
          state: data.state || '',
          country: data.country || 'India',
          occupation: data.profession || '',
          organization: data.organization || '',
          designation: data.designation || '',
        }));
      } else {
        // Token invalid or expired
        localStorage.removeItem('professionalToken');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (field, value) => {
    const currentValues = formData[field];
    if (currentValues.includes(value)) {
      setFormData({
        ...formData,
        [field]: currentValues.filter((v) => v !== value),
      });
    } else {
      setFormData({
        ...formData,
        [field]: [...currentValues, value],
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('professionalToken');
      await axios.post(`${API}/conclave-registration`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Registration submitted successfully! We will contact you with event details.');
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while checking authentication
  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login prompt
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-lg mx-auto">
          <Card className="p-8 md:p-12 shadow-floating text-center">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-primary" />
            </div>
            
            <h1 className="font-serif font-bold text-2xl md:text-3xl text-primary mb-4">
              Professional Login Required
            </h1>
            
            <p className="text-muted-foreground mb-6">
              Only registered and approved professionals can register for Khatri Conclave 2026. 
              Please login with your professional account to continue.
            </p>

            <div className="bg-muted p-4 rounded-lg mb-6 text-left">
              <p className="text-sm font-medium mb-2">To register for Conclave:</p>
              <ol className="text-sm text-muted-foreground space-y-1">
                <li>1. Register as a Professional (if not already)</li>
                <li>2. Wait for admin approval</li>
                <li>3. Login with your approved credentials</li>
                <li>4. Complete the Conclave registration form</li>
              </ol>
            </div>

            <div className="space-y-3">
              <Link to="/professional/login" className="block">
                <Button className="w-full bg-primary hover:bg-primary/90">
                  <LogIn size={18} className="mr-2" />
                  Login as Professional
                </Button>
              </Link>
              
              <Link to="/professional/register" className="block">
                <Button variant="outline" className="w-full">
                  <User size={18} className="mr-2" />
                  Register as Professional
                </Button>
              </Link>

              <Link to="/" className="block">
                <Button variant="ghost" className="w-full text-muted-foreground">
                  <ArrowLeft size={16} className="mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Success state after submission
  if (submitted) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Card className="p-12 shadow-floating">
            <div className="mb-6">
              <CheckCircle2 className="w-20 h-20 text-green-600 mx-auto mb-4" />
              <h1 className="font-serif font-bold text-4xl text-primary mb-4">
                Conclave Registration Submitted!
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                Thank you for registering for Khatri Conclave 2026, {professional?.fullName}!
              </p>
              <div className="bg-muted p-6 rounded-lg mb-6">
                <p className="text-sm text-foreground mb-2">
                  <strong>What's Next?</strong>
                </p>
                <ul className="text-sm text-muted-foreground text-left space-y-2">
                  <li>• Your registration has been linked to your Professional ID: <strong>{professional?.professionalId}</strong></li>
                  <li>• Conclave 2026 details will be shared as they are finalized</li>
                  <li>• Event updates will be sent to {formData.email}</li>
                  <li>• For any queries, contact us at brahmkhatrimahasabha@gmail.com or 9414134392</li>
                </ul>
              </div>
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => navigate('/')}
                  className="bg-primary hover:bg-primary/90"
                >
                  Back to Home
                </Button>
                <Button
                  onClick={() => navigate('/professional/directory')}
                  variant="outline"
                >
                  Go to Directory
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Main registration form (for authenticated professionals)
  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Card className="p-8 md:p-12 shadow-floating">
          {/* Header */}
          <div className="text-center mb-12 border-b-2 border-secondary pb-8">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm mb-4">
              <CheckCircle2 size={16} />
              Logged in as: {professional?.fullName}
              {professional?.professionalId && (
                <span className="bg-green-200 px-2 py-0.5 rounded text-xs font-mono">
                  ID: {professional.professionalId}
                </span>
              )}
            </div>
            <h1 className="font-serif font-bold text-3xl md:text-4xl text-primary mb-3">
              Khatri Conclave 2026 Registration
            </h1>
            <h2 className="font-serif font-bold text-xl md:text-2xl text-secondary mb-4">
              Event Registration Form
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Complete your registration for the upcoming Khatri Conclave 2026. Your professional details have been pre-filled.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information - Pre-filled & Read-only */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2">
                Personal Information (from your profile)
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={formData.fullName}
                    disabled
                    className="bg-muted"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Mobile Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  disabled
                  className="bg-muted"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    type="text"
                    value={formData.city}
                    disabled
                    className="bg-muted"
                  />
                </div>

                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    name="state"
                    type="text"
                    value={formData.state}
                    disabled
                    className="bg-muted"
                  />
                </div>

                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    name="country"
                    type="text"
                    value={formData.country}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
            </div>

            {/* Professional Information - Pre-filled & Read-only */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2">
                Professional Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="occupation">Occupation/Profession</Label>
                  <Input
                    id="occupation"
                    name="occupation"
                    type="text"
                    value={formData.occupation}
                    disabled
                    className="bg-muted"
                  />
                </div>

                <div>
                  <Label htmlFor="organization">Organization/Company</Label>
                  <Input
                    id="organization"
                    name="organization"
                    type="text"
                    value={formData.organization}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="designation">Designation/Role</Label>
                <Input
                  id="designation"
                  name="designation"
                  type="text"
                  value={formData.designation}
                  disabled
                  className="bg-muted"
                />
              </div>
            </div>

            {/* Event Preferences */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2">
                Event Preferences
              </h3>

              <div>
                <p className="text-sm font-medium mb-3">
                  What areas of community engagement are you most interested in? (Select all that apply)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {interestAreas.map((area) => (
                    <label key={area} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-muted rounded">
                      <input
                        type="checkbox"
                        checked={formData.areasOfInterest.includes(area)}
                        onChange={() => handleCheckboxChange('areasOfInterest', area)}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm">{area}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-3">
                  Which session types would you prefer to attend? (Select all that apply)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {sessionPreferences.map((pref) => (
                    <label key={pref} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-muted rounded">
                      <input
                        type="checkbox"
                        checked={formData.attendancePreference.includes(pref)}
                        onChange={() => handleCheckboxChange('attendancePreference', pref)}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm">{pref}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2">
                Additional Information
              </h3>

              <div>
                <Label htmlFor="accompanyingPersons">
                  Number of Accompanying Persons (Family Members)
                </Label>
                <Input
                  id="accompanyingPersons"
                  name="accompanyingPersons"
                  type="number"
                  min="0"
                  max="10"
                  placeholder="0"
                  value={formData.accompanyingPersons}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Label htmlFor="specialRequirements">
                  Special Requirements or Comments
                </Label>
                <Textarea
                  id="specialRequirements"
                  name="specialRequirements"
                  placeholder="Any dietary requirements, accessibility needs, or other comments..."
                  value={formData.specialRequirements}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/professional/directory')}
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to Directory
              </Button>
              <Button
                type="submit"
                className="bg-primary hover:bg-primary/90 flex-1"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Register for Conclave 2026'
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ConclaveRegistration;
